@if (!isOpen) {
  <!--
    The shared FAB owns the frame and the vertical drag (resting height → middle
    of the viewport, remembered per device under `storageKey`); the violet skin
    is handed down from this component's stylesheet. `activated` is a click that
    did not turn into a drag.
  -->
  <draggable-fab
    class="chat-fab"
    storageKey="trendmulti.chat.bubble.offsetPx"
    [ariaLabel]="'chat.bubble.open' | translate"
    (activated)="openPopup()"
  >
    <i class="chat-fab__icon ri-chat-3-line font-24" aria-hidden="true"></i>
    @if (((chatBadge.totalUnread$ | async) ?? 0) > 0) {
      <span class="chat-fab__badge badge rounded-pill bg-danger">
        {{ chatBadge.badgeText$ | async }}
      </span>
    }
  </draggable-fab>
}

@if (isOpen) {
  <section
    class="chat-popup card shadow-lg"
    role="dialog"
    [attr.aria-label]="'chat.bubble.title' | translate"
  >
    <div
      class="chat-popup__header d-flex align-items-center justify-content-between gap-3 p-2"
    >
      <div class="d-flex align-items-center gap-1">
        @if (popupChannelId || popupDirectUserId) {
          <a
            href="javascript:void(0);"
            class="chat-popup__icon-action flex-shrink-0"
            (click)="backToList()"
            [matTooltip]="'chat.conversation.back' | translate"
            [attr.aria-label]="'chat.conversation.back' | translate"
          >
            <i class="ri-arrow-left-line font-18"></i>
          </a>
        }
        <button
          type="button"
          class="chat-popup__me chat-popup__avatar chat-popup__avatar--me flex-shrink-0"
          (click)="openStatusPicker()"
          [attr.aria-label]="'chat.status.open' | translate"
        >
          @if (currentUser?.image) {
            <img
              [src]="$safeNavigationMigration(currentUser?.image)"
              alt=""
              aria-hidden="true"
              (error)="onImageError($event)"
            />
          } @else {
            {{ resolveInitials(currentUser?.name ?? "") }}
          }
          <span
            class="chat-popup__dot rounded-circle"
            [ngClass]="myPresence.dotClass"
            aria-hidden="true"
          ></span>
        </button>
        <chat-status-button class="d-block overflow-hidden"></chat-status-button>
      </div>

      <div
        class="chat-popup__actions d-flex align-items-center gap-1 flex-shrink-0"
      >
        <a
          href="javascript:void(0);"
          class="chat-popup__icon-action"
          (click)="openNewDirect()"
          [matTooltip]="'chat.sidebar.newConversation' | translate"
          [attr.aria-label]="'chat.sidebar.newConversation' | translate"
        >
          <i class="ri-chat-new-line"></i>
        </a>
        @if (canCreateChannel) {
          <a
            href="javascript:void(0);"
            class="chat-popup__icon-action"
            (click)="openNewChannel()"
            [matTooltip]="'chat.channel.create' | translate"
            [attr.aria-label]="'chat.channel.create' | translate"
          >
            <i class="ri-add-circle-line"></i>
          </a>
        }

        <span class="chat-popup__actions-divider" aria-hidden="true"></span>

        <!-- The full chat lives in the chat app now (RF-13): this hands the open
             conversation over to it and minimizes the window. -->
        <a
          href="javascript:void(0);"
          class="chat-popup__icon-action"
          (click)="openInChatApp()"
          [matTooltip]="'chat.launcher.openApp' | translate"
          [attr.aria-label]="'chat.launcher.openApp' | translate"
        >
          <i class="ri-external-link-line"></i>
        </a>
        <!-- Notification preferences: quiet hours + sound (T-21 · RF-36) -->
        <a
          href="javascript:void(0);"
          class="chat-popup__icon-action"
          (click)="openPreferences()"
          [matTooltip]="'chat.shell.preferences' | translate"
          [attr.aria-label]="'chat.shell.preferences' | translate"
        >
          <i class="ri-notification-3-line"></i>
        </a>
        <a
          href="javascript:void(0);"
          class="chat-popup__icon-action"
          (click)="minimizePopup()"
          [matTooltip]="'chat.bubble.close' | translate"
          [attr.aria-label]="'chat.bubble.close' | translate"
        >
          <i class="ri-close-line"></i>
        </a>
      </div>
    </div>

    <chat-connection-banner></chat-connection-banner>

    <div class="card-body p-0 chat-popup__body">
      @if (popupDirectUserId) {
        <chat-conversation
          class="d-block h-100"
          [channelId]="null"
          [directUserId]="popupDirectUserId"
          [compact]="true"
        ></chat-conversation>
      } @else if (popupChannelId) {
        <!--
          No toolbar over an open conversation any more: searching inside it moved
          into the pane's own header kebab, next to the contact card, where a hit
          jumps to the message in place instead of replacing the pane with a
          results list. The search here is the CONVERSATION-LIST search only.
        -->
        <chat-conversation
          class="d-block h-100"
          [channelId]="popupChannelId"
          [focusMessageId]="popupFocusMessageId"
          [compact]="true"
        ></chat-conversation>
      } @else if (chatUnavailable$ | async) {
        <div class="chat-popup__scroll">
          <chat-unavailable
            [compact]="true"
            (retry)="chatBadge.refresh()"
          ></chat-unavailable>
        </div>
      } @else if (bootstrapFailed$ | async) {
        <div class="chat-popup__scroll">
          <chat-error-state
            [compact]="true"
            (retry)="chatBadge.refresh()"
          ></chat-error-state>
        </div>
      } @else {
        <chat-conversation-list
          variant="popup"
          (conversationSelected)="openChannel($event)"
          (messageHitSelected)="openChannel($event.channelId, $event.messageId)"
          (paneSelected)="openFullViewPanel($event)"
        ></chat-conversation-list>
      }
    </div>
  </section>
}
